import isNotUndefinedOrNull from '@zenbusiness/application-commons-utils/isNotUndefinedOrNull';
import { EXTEND_DURATION_MINUTES } from "./constants.mjs";
import { WebAuth } from "./auth0.mjs";
import AuthError from "./auth-error.mjs";
import AuthStorage from "./auth-storage.mjs";
import ErrorWithCode from "./error-with-code.mjs";
/**
 * @typedef ClientOptions
 * @property {string} clientID
 * @property {string} domain
 * @property {string} audience
 * @property {string} [connection]
 * @property {string} [responseType]
 * @property {string} [scope]
 * @property {string} [loginUri] URI path of the login page
 * @property {string} [logoutUri] URI path of the logout page
 * @property {string} [callbackUri] URI path of the callback page.
 * @property {string} [redirectUri] Full url auth0 should return to after login
 */
export default class AuthenticationClient {
  constructor( /** @type {ClientOptions} */options) {
    this.options = options;
    const {
      clientID,
      domain,
      audience,
      responseType = 'token id_token',
      scope,
      redirectUri = `${document.location.origin}${options.callbackUri}`
    } = options;
    this.webAuth = new WebAuth({
      clientID,
      domain,
      audience,
      responseType,
      scope,
      redirectUri
    });
  }
  async callback() {
    const {
      loginState: state
    } = AuthStorage;
    if (!state) {
      throw new ErrorWithCode({
        message: 'Callback invoked without login state.',
        code: 'empty_login_state'
      });
    }
    const {
      hash
    } = window.location;
    if (!hash) {
      AuthStorage.clean();
      throw new Error(`Callback invoked without hash: ${JSON.stringify(window.location)}`);
    }
    const auth = await this.webAuth.parseHash({
      hash,
      state
    }).catch(err => {
      AuthStorage.clean();
      throw new AuthError(err);
    });
    if (!auth || !auth.expiresIn) {
      AuthStorage.clean();
      throw new Error(`Invalid authentication for state: ${state}, hash: ${hash}, auth: ${JSON.stringify(auth)}`);
    }
    return AuthStorage.processAuthSuccess(auth);
  }
  login(destination) {
    const stateValue = `zen-${Math.floor(Math.random() * 1000000)}`;
    AuthStorage.loginState = stateValue;
    if (isNotUndefinedOrNull(destination)) {
      AuthStorage.redirectTo = destination;
    }
    this.webAuth.authorize({
      connection: this.options.connection,
      state: stateValue,
      nonce: stateValue
    });
  }
  logout({
    redirectTo
  }) {
    const returnTo = new URL(this.options.loginUri, window.location.origin);
    if (isNotUndefinedOrNull(redirectTo)) {
      returnTo.searchParams.set('redirectTo', redirectTo);
    }
    AuthStorage.clean();
    this.webAuth.logout({
      returnTo: returnTo.toString()
    });
  }
  async checkSession() {
    const nonce = `zen-${Math.floor(Math.random() * 1000000)}`;
    let auth;
    try {
      auth = await this.webAuth.checkSession({
        nonce,
        state: nonce
      });
    } catch (err) {
      if (err.code && err.code === 'login_required') {
        AuthStorage.clean();
        // Not authenticated
        return AuthStorage.toJSON();
      }
      AuthStorage.clean();
      throw new AuthError(err);
    }
    if (!auth || !auth.expiresIn) {
      AuthStorage.clean();
      throw new Error(`Invalid authentication during checkSession for state: ${nonce}, auth: ${JSON.stringify(auth)}`);
    }
    return AuthStorage.processAuthSuccess(auth);
  }

  /**
   * Extend user session during activity.
   * @returns {Promise<import('./auth-storage').AuthenticationState>}
   */
  async extendSession() {
    const {
      issueDate,
      loginState
    } = AuthStorage;
    if (!issueDate) return AuthStorage.toJSON();

    // don't extend if we're mid-auth
    if (loginState) return AuthStorage.toJSON();
    const minutesSince = (new Date().valueOf() - issueDate.valueOf()) / 60000;
    if (minutesSince >= EXTEND_DURATION_MINUTES) {
      return this.checkSession();
    }
    return AuthStorage.toJSON();
  }
  async resetPassword(username, destination) {
    if (isNotUndefinedOrNull(destination)) {
      AuthStorage.redirectTo = destination;
    }
    await this.webAuth.changePassword({
      email: username,
      connection: 'Username-Password-Authentication'
    }).catch(err => {
      AuthStorage.clean();
      throw new AuthError(err);
    });
  }
}